import {
  Bell,
  BookOpen,
  Building2,
  BriefcaseBusiness,
  CalendarDays,
  CheckSquare,
  Clock3,
  CreditCard,
  Crown,
  FileText,
  Globe,
  LayoutDashboard,
  Layers3,
  LineChart,
  Mail,
  MessageSquare,
  ListTodo,
  LockKeyhole,
  Puzzle,
  Receipt,
  Rocket,
  SquareGanttChart,
  UserCircle2,
  Users,
  Workflow,
  LayoutTemplate,
} from 'lucide-vue-next'
import {
  isNotificationUnread,
  matchesNotificationContext,
  matchesNotificationMute,
  notificationPageKey,
  type NotificationPageKey,
} from '~/lib/notifications'

export type AppNavItem = {
  title: string
  url?: string
  icon: any
  badge?: string
  notificationCount?: number
  disabled?: boolean
  action?: () => void
}

export type AppNavGroup = {
  id: string
  label?: string
  items: AppNavItem[]
}

export type AppNavResolvedItem = AppNavItem & {
  groupId: string
  groupLabel: string
}

export function useAppNavigation() {
  const route = useRoute()
  const subscription = useWorkspaceSubscription()
  const notificationStore = useNotificationStore()
  const tenantStore = useTenantStore()
  const { tenantPath } = useTenantPath()
  const tenantNotificationContext = computed(() => ({
    surface: 'tenant' as const,
    tenantId: tenantStore.currentTenantId || null,
  }))
  const pageNotificationCounts = computed<Record<NotificationPageKey, number>>(() => {
    const counts: Record<NotificationPageKey, number> = {
      messages: 0,
      tasks: 0,
      projects: 0,
      clients: 0,
      quotes: 0,
      invoices: 0,
    }

    for (const notification of notificationStore.items) {
      if (!isNotificationUnread(notification)) continue
      if (Array.isArray(notification.channels) && !notification.channels.includes('in_app')) continue
      if (!matchesNotificationContext(notification, tenantNotificationContext.value)) continue
      if (notificationStore.mutes.some(mute => matchesNotificationMute(notification, mute))) continue

      const page = notificationPageKey(notification)
      if (page) counts[page] += 1
    }

    return counts
  })
  const unreadForPage = (page: NotificationPageKey) => pageNotificationCounts.value[page]
  const primaryItems = computed<AppNavItem[]>(() => ([
    {
      title: 'Dashboard',
      url: tenantPath('/my-hub'),
      icon: LayoutDashboard,
      badge: 'You',
    },
    {
      title: 'Inbox',
      url: tenantPath('/messages'),
      icon: MessageSquare,
      notificationCount: unreadForPage('messages'),
    },
    {
      title: 'Tasks',
      url: tenantPath('/tasks'),
      icon: CheckSquare,
      notificationCount: unreadForPage('tasks'),
    },
    {
      title: 'Projects',
      url: tenantPath('/projects'),
      icon: SquareGanttChart,
      notificationCount: unreadForPage('projects'),
    },
    {
      title: 'Clients',
      url: tenantPath('/clients'),
      icon: BriefcaseBusiness,
      notificationCount: unreadForPage('clients'),
    },
    {
      title: 'Calendar',
      url: tenantPath('/calendar'),
      icon: CalendarDays,
    },
  ]))

  const navGroups = computed((): AppNavGroup[] => [
    {
      id: 'workspace',
      items: primaryItems.value,
    },
    {
      id: 'processes',
      label: 'Processes',
      items: [
        {
          title: 'Onboarding',
          url: tenantPath('/onboarding'),
          icon: Rocket,
        },
        {
          title: 'Pipelines',
          url: tenantPath('/onboarding/pipelines'),
          icon: Layers3,
        },
        {
          title: 'Workflows',
          url: tenantPath('/workflows'),
          icon: Workflow,
          badge: subscription.canUseWorkflows.value ? undefined : subscription.requiredLabelFor('workflows'),
        },
      ],
    },
    {
      id: 'tools',
      label: 'Tools',
      items: [
        {
          title: 'Forms',
          url: tenantPath('/forms'),
          icon: FileText,
        },
        {
          title: 'Time tracking',
          url: tenantPath('/time'),
          icon: Clock3,
        },
        {
          title: 'Documentation',
          url: tenantPath('/documentation'),
          icon: BookOpen,
        },
        {
          title: 'Proposals',
          url: tenantPath('/proposals'),
          icon: BriefcaseBusiness,
        },
        {
          title: 'Contract templates',
          url: tenantPath('/contracts/templates'),
          icon: FileText,
        },
      ],
    },
    {
      id: 'revenue',
      label: 'Revenue',
      items: [
        {
          title: 'Services',
          url: tenantPath('/services'),
          icon: Layers3,
        },
        {
          title: 'Analytics',
          url: tenantPath('/analytics'),
          icon: LineChart,
          badge: subscription.canUseAnalytics.value ? undefined : subscription.requiredLabelFor('analytics'),
        },
        {
          title: 'Quotes',
          url: tenantPath('/quotes'),
          icon: FileText,
          notificationCount: unreadForPage('quotes'),
        },
        {
          title: 'Invoices',
          url: tenantPath('/invoices'),
          icon: Receipt,
          notificationCount: unreadForPage('invoices'),
        },
      ],
    },
    {
      id: 'team',
      label: 'Team',
      items: [
        { title: 'Team overview', url: tenantPath('/team'), icon: Users },
        { title: 'Members & roles', url: tenantPath({ path: '/team', query: { view: 'members' } }), icon: UserCircle2 },
        { title: 'Availability & capacity', url: tenantPath('/services/capacity'), icon: CalendarDays },
      ],
    },
  ])

  const settingsNavGroups = computed((): AppNavGroup[] => [
    {
      id: 'workspace-settings',
      label: 'Workspace settings',
      items: [
        {
          title: 'Subscription',
          url: tenantPath('/settings/subscription'),
          icon: Crown,
        },
        {
          title: 'Company',
          url: tenantPath('/settings/company'),
          icon: Building2,
        },
        {
          title: 'Payments',
          url: tenantPath('/settings/payments'),
          icon: CreditCard,
        },
        {
          title: 'Email',
          url: tenantPath('/settings/email'),
          icon: Mail,
          badge: subscription.canUseCustomEmailDomains.value ? undefined : 'Pro',
        },
        {
          title: 'Channels',
          url: tenantPath('/settings/channels'),
          icon: MessageSquare,
        },
        {
          title: 'Client Portal',
          url: tenantPath('/settings/client-portal'),
          icon: LayoutTemplate,
        },
        {
          title: 'Tasks',
          url: tenantPath('/settings/tasks'),
          icon: ListTodo,
        },
        {
          title: 'Domains',
          url: tenantPath('/settings/domains'),
          icon: Globe,
          badge: subscription.canUseCustomDomains.value ? undefined : 'Upgrade',
        },
      ],
    },
    {
      id: 'personal-settings',
      label: 'Personal settings',
      items: [
        {
          title: 'Profile',
          url: tenantPath('/account'),
          icon: UserCircle2,
        },
        {
          title: 'Notifications',
          url: tenantPath('/account/notifications'),
          icon: Bell,
        },
        {
          title: 'Integrations',
          url: tenantPath('/account/integrations'),
          icon: Puzzle,
        },
        {
          title: 'Security',
          url: tenantPath('/account/security'),
          icon: LockKeyhole,
        },
      ],
    },
  ])

  const allNavGroups = computed(() => [...navGroups.value, ...settingsNavGroups.value])

  const queryVariantPaths = computed(() => {
    const set = new Set<string>()
    for (const group of allNavGroups.value) {
      for (const item of group.items) {
        if (!item.url || !item.url.includes('?')) continue
        const [path] = item.url.split('?')
        if (path) set.add(path)
      }
    }
    return set
  })

  function isItemActive(_groupLabel: string, url: string): boolean {
    const [path, queryString] = url.split('?')
    if (!path) return false

    const pathIsActive = route.path === path || route.path.startsWith(`${path}/`)
    if (!pathIsActive) return false

    if (route.path !== path) {
      const hasMoreSpecificMatch = allNavGroups.value.some((group) => (
        group.items.some((item) => {
          const itemPath = item.url?.split('?')[0]
          return Boolean(
            itemPath
            && itemPath !== path
            && itemPath.startsWith(`${path}/`)
            && (route.path === itemPath || route.path.startsWith(`${itemPath}/`)),
          )
        })
      ))

      if (hasMoreSpecificMatch) return false
    }

    if (!queryString) {
      if (queryVariantPaths.value.has(path) && Object.keys(route.query).length > 0) {
        return false
      }

      return true
    }

    const params = new URLSearchParams(queryString)

    for (const [key, value] of params.entries()) {
      if (String(route.query[key] || '') !== value) {
        return false
      }
    }

    return true
  }

  const flatNavItems = computed<AppNavResolvedItem[]>(() => (
    allNavGroups.value.flatMap((group) => (
      group.items.map((item) => ({
        ...item,
        groupId: group.id,
        groupLabel: group.label || 'Workspace',
      }))
    ))
  ))

  const activeItem = computed<AppNavResolvedItem | null>(() => (
    flatNavItems.value.find((item) => item.url && isItemActive(item.groupLabel, item.url)) || null
  ))

  const activeGroupLabel = computed(() => activeItem.value?.groupLabel || '')

  return {
    primaryItems,
    navGroups,
    settingsNavGroups,
    isItemActive,
    activeGroupLabel,
    flatNavItems,
    activeItem,
  }
}
